import { useCallback, useEffect, useRef, useState } from 'react';
import {
    onAuthStateChanged,
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    signInWithPopup,
    signOut,
    sendPasswordResetEmail,
    updateProfile,
    getMultiFactorResolver,
} from 'firebase/auth';
import { auth, googleProvider } from '../config/firebase';
import apiService from '../services/apiService';
import { getActiveTimezone } from '../utils/timezone';
import { reconcilePreferences, trackPreferenceSave } from '../utils/preferenceSync';
import {
    armSignupBarrier,
    awaitSignupBarrier,
    clearPendingSignupUsername,
    normalizeUsername,
    pendingSignupUsername,
    resolveSignupBarrier,
} from '../utils/signupIdentity';
import { useLanguage, storedLanguage } from '../i18n';
import { getAuthToken } from '../utils/authToken';
import { standardErrorMessage } from '../utils/userError';
import { isMfaRequired, markMfaSignOut } from '../utils/authEvents';

// sessionStorage marker so the sign-in is reported once per browser session
// rather than on every page load (see the call site for why).
const LOGIN_EVENT_KEY = 'omnis:login-reported';

// Hot reload (#397) — the mutable profile fields an `omnis:profile-updated`
// event may carry into the live `user` object. Both are strings the server
// returns from `PATCH /auth/profile`, so a merge can be a plain assignment; a
// whitelist (rather than a blind spread) keeps the event from ever inventing a
// key on the user shape or overwriting `id`/`role`/an onboarding gate.
const PROFILE_EVENT_FIELDS = ['username', 'timezone'];

/**
 * Attempt to refresh the Firebase ID token, swap the new one into
 * localStorage, and return it (or null on failure). Tolerant of transient
 * failures — used both by the proactive periodic / visibility handlers and
 * the reactive 401 recovery in App.jsx. Never throws — always returns.
 */
export async function tryRefreshFirebaseToken() {
    const currentUser = auth.currentUser;
    if (!currentUser) return null;
    try {
        const newToken = await currentUser.getIdToken(true);
        if (!newToken) return null;
        try {
            localStorage.setItem('authToken', newToken);
            localStorage.setItem('token', newToken);
        } catch (_) { /* ignore */ }
        return newToken;
    } catch (err) {
        // Network not up yet on wake / Firebase backend hiccup / token
        // revoked. Caller decides whether this is fatal.
        return null;
    }
}

/**
 * Best-effort profile side effects, deferred until they are ALLOWED.
 *
 * Both of these are account plumbing rather than user actions, and both
 * go through `PATCH /auth/profile` — which the onboarding gates refuse
 * for an account that has not finished signing up (#335 email, #374
 * salary). So neither is done once-and-forgotten: this runs on every
 * profile read (login AND `refreshProfile`, which is exactly what the
 * verification and salary screens call when their gate lifts), and a
 * refusal simply leaves the work pending for the next pass.
 *
 *  • #373 the typed signup username, when the `name` claim did not make
 *    it onto the row (the barrier is the primary mechanism — on the happy
 *    path the name already matches and nothing is sent).
 *  • #181 the live IANA timezone, the durable source of truth the
 *    day-anchored channels resolve times against (they have no browser
 *    clock). Without the retry a brand-new account stayed on 'UTC' for
 *    its whole first session.
 *
 * Returns the profile, replaced by the server's updated copy when a
 * PATCH succeeded.
 */
/** How many times a profile read is retried before the app gives up and asks.
 *  The gates cannot be resolved without it (#402), so a transient blip must not
 *  become either a locked-out user or an unlocked workspace. */
const PROFILE_READ_ATTEMPTS = 3;

async function readProfileWithRetry() {
    let lastError;
    for (let attempt = 1; attempt <= PROFILE_READ_ATTEMPTS; attempt += 1) {
        try {
            return await apiService.getUserProfile();
        } catch (e) {
            lastError = e;
            // 2FA (#333): a single-factor token on a 2FA account is refused
            // every time — retrying only delays the sign-out below.
            if (isMfaRequired(e)) break;
            if (attempt < PROFILE_READ_ATTEMPTS) {
                // 400ms, 800ms — long enough to ride out a cold start or a lost
                // packet, short enough that a real outage is surfaced promptly.
                await new Promise(r => setTimeout(r, 400 * attempt));
            }
        }
    }
    throw lastError;
}

async function syncProfileSideEffects(profile) {
    let current = profile;

    const typedName = pendingSignupUsername();
    if (typedName && current?.username !== typedName) {
        try {
            current = await apiService.patch('/auth/profile', { username: typedName });
            clearPendingSignupUsername();
        } catch (_) {
            // Gated (403) or offline — keep it pending and try again on
            // the next profile read. The user can also rename in Settings.
        }
    } else if (typedName) {
        clearPendingSignupUsername();   // already correct — nothing to do
    }

    // Synced Settings preferences (#480): adopt the server's values into the
    // local cache (server = source of truth), migrate local-only ones up once.
    // Must run BEFORE the timezone push below, so an adopted/migrated pin is
    // what getActiveTimezone() reads (the server ignores auto pushes while a pin
    // exists anyway). Best-effort — retried on every profile read.
    if (current && current.preferences && typeof current.preferences === 'object') {
        try {
            current = await reconcilePreferences(current);
        } catch (_) { /* best-effort */ }
    } else {
        // A backend without the #480 `preferences` block: the 2026-10-06
        // behaviour (mirror the chosen UI language up).
        try {
            const lang = storedLanguage();
            if (lang && lang !== (current?.language || 'en')) {
                const updated = await apiService.patch('/auth/profile', { language: lang });
                if (updated) current = updated;
            }
        } catch (_) { /* best-effort */ }
    }

    try {
        const activeTz = getActiveTimezone();
        if (activeTz && activeTz !== (current?.timezone || 'UTC')) {
            const updated = await apiService.patch('/auth/profile', { timezone: activeTz });
            if (updated) current = updated;
        }
    } catch (_) { /* best-effort — retried on the next profile read */ }

    return current;
}

// A language change made while signed in is pushed at once. Registered at MODULE
// scope, exactly once: useAuth is a plain hook with many live instances, and a
// per-instance listener would PATCH once per instance.
let languageSyncInstalled = false;
export function pushLanguageToProfile(lang) {
    if ((lang !== 'en' && lang !== 'es') || !getAuthToken()) return Promise.resolve(false);
    // Tracked (#480) so a profile read racing this push can't adopt the old value.
    return trackPreferenceSave('language', apiService.patch('/auth/profile', { language: lang }))
        .then(() => true)
        .catch(() => false);   // retried by syncProfileSideEffects on the next read
}
if (typeof window !== 'undefined' && !languageSyncInstalled) {
    languageSyncInstalled = true;
    window.addEventListener('omnis:language-changed', (e) => {
        // #480 — a value ADOPTED from the server (or a failed save's revert)
        // must not be pushed straight back.
        const source = e?.detail?.source;
        if (source === 'server' || source === 'revert') return;
        pushLanguageToProfile(e?.detail?.language);
    });
}

const useAuth = () => {
    // Auth failures are the FIRST copy a new Spanish user ever reads, and the login
    // screen is rendered before any panel — so these messages are localized here at
    // the source rather than being re-mapped in LoginPage/SignupPage. Only the
    // human-readable text is translated; `error.code` ('auth/wrong-password', …) is
    // Firebase's contract and is never touched.
    const { t } = useLanguage();
    const [token, setToken] = useState(null);
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [authStable, setAuthStable] = useState(false);
    // Has a REAL backend profile landed at least once for the signed-in user?
    //
    // 🛑 THIS IS A SECURITY-RELEVANT TRI-STATE, not a convenience (Salary Gate
    // Bypass #402). The onboarding gates in App.jsx are computed from `user`, so
    // "we have not read the profile yet" and "the profile says nothing is
    // outstanding" used to be the SAME thing — falsy — and both unlocked the
    // workspace. That made every gate fail OPEN: a cold page load unlocked the
    // app for the few hundred ms before the fetch landed, and a fetch that
    // FAILED unlocked it indefinitely, because the catch below installed a
    // Firebase-only fallback user carrying no onboarding keys and nothing ever
    // re-read the profile afterwards. Prod 2026-09-23 23:26:27 shows exactly
    // that: the profile 500'd, and 100ms later the workspace's own panel calls
    // (/api/contacts/requests, /api/teams, /api/reminders, /api/calendar/activity)
    // were being refused 403 email_unverified — i.e. the workspace had mounted
    // for an account that had not even confirmed its address.
    //
    // With this flag the unknown state is DISTINCT and App.jsx holds a loader
    // instead of guessing. It never flashes the wrong screen in either
    // direction: not a gate in front of an established account, and not the
    // workspace in front of an un-onboarded one.
    const [profileLoaded, setProfileLoaded] = useState(false);
    const [profileError, setProfileError] = useState(null);
    // Once we've seen a signed-in user at least once during this app
    // lifecycle, a subsequent null auth-state emission is treated with
    // suspicion (could be a transient init re-emission after wake) and
    // we delay the localStorage wipe so a quick re-emission with the
    // real user doesn't bounce the UI to /login.
    const hasSeenUserRef = useRef(false);
    const pendingSignOutRef = useRef(null);

    useEffect(() => {

        // Listen to Firebase auth state changes
        // Note: Don't clear tokens here - auth.currentUser is null on page load
        // The authStable flag handles race conditions instead
        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
            if (firebaseUser) {
                // Cancel any in-flight "transient null" sign-out — Firebase
                // re-emitted with the real user before the grace period ended.
                if (pendingSignOutRef.current) {
                    clearTimeout(pendingSignOutRef.current);
                    pendingSignOutRef.current = null;
                }
                hasSeenUserRef.current = true;
                try {
                    // #373 — a signup in flight is still writing the typed
                    // display name onto the Firebase account. Wait for it
                    // (bounded) BEFORE minting the token below, so the token
                    // that CREATES the backend row already carries `name` and
                    // the row is named correctly on its first write rather than
                    // being corrected afterwards. A no-op on every ordinary
                    // login (nothing is armed), and bounded so a hung
                    // updateProfile can never block a sign-in indefinitely.
                    await awaitSignupBarrier();

                    // Get Firebase ID token (force refresh to ensure it's valid)
                    const idToken = await firebaseUser.getIdToken(true);
                    console.log('Got Firebase token:', idToken ? `${idToken.substring(0, 20)}...` : 'NULL');

                    // Store token for API calls BEFORE updating React state
                    // This prevents race conditions where other hooks read from
                    // localStorage before it's updated
                    localStorage.setItem('authToken', idToken);
                    localStorage.setItem('token', idToken);
                    console.log('Token stored in localStorage:', localStorage.getItem('token')?.substring(0, 20));

                    // Now update React state (may trigger re-renders)
                    setToken(idToken);

                    // Small delay to ensure token propagation
                    await new Promise(resolve => setTimeout(resolve, 100));

                    // Fetch user profile from backend (this also creates user if new)
                    console.log('Calling getUserProfile with token:', localStorage.getItem('token')?.substring(0, 20));
                    let profile = await readProfileWithRetry();

                    // The deferred plumbing: the typed signup username (#373,
                    // fallback only — the barrier above normally means the row
                    // was already created with it) and the live timezone (#181).
                    profile = await syncProfileSideEffects(profile);

                    setUser({
                        id: profile.id,
                        username: profile.username,
                        email: profile.email,
                        role: profile.role,
                        timezone: profile.timezone || 'UTC',
                        // #335 — whether this account still has to confirm its
                        // email. App.jsx renders the code screen instead of the
                        // workspace while it does. Undefined until the profile
                        // lands, so there is no gate flash on a page load.
                        emailVerification: profile.email_verification || null,
                        // #374 — step 3 of onboarding. Same contract: undefined
                        // until the profile lands, so the Salary Selection page
                        // can never flash in front of an established account.
                        salarySelection: profile.salary_selection || null,
                    });
                    // A real profile landed — the onboarding gates may now be
                    // trusted, in EITHER direction (#402).
                    setProfileError(null);
                    setProfileLoaded(true);

                    // #335 — report the sign-in so a NEW device raises a
                    // security alert. The backend has no login event of its own
                    // (auth is entirely client-side in Firebase, and
                    // get_or_create_user runs on every request, not just the
                    // first), so this is the signal.
                    //
                    // Once per browser SESSION, not per page load: this callback
                    // also fires when a cached session rehydrates, and a request
                    // on every reload for every user would be real traffic for a
                    // device the server has already recorded. sessionStorage is
                    // empty in exactly the case that matters — a genuinely new
                    // browser session — and the device row is idempotent anyway,
                    // so the worst case of a missed dedupe is one extra no-op.
                    try {
                        if (!sessionStorage.getItem(LOGIN_EVENT_KEY)) {
                            sessionStorage.setItem(LOGIN_EVENT_KEY, '1');
                            apiService.post('/auth/login-event', {})
                                .catch(() => { /* telemetry — never block a login */ });
                        }
                    } catch (_) { /* sessionStorage can throw in private mode */ }

                    // (The timezone push that used to live here moved into
                    // syncProfileSideEffects above — it has to be RETRIED once the
                    // onboarding gates lift, or a brand-new account stays on 'UTC'
                    // for its whole first session. See that function.)

                    // Mark auth as stable - safe to respond to 401s now
                    setAuthStable(true);
                } catch (error) {
                    console.error('Error fetching user profile:', error);
                    // 2FA (#333): this session was signed in WITHOUT the second
                    // factor (before 2FA was turned on, on another device). It
                    // can never read the profile; sign it out so the login page
                    // runs the challenge, rather than parking it on "couldn't
                    // load your account".
                    if (isMfaRequired(error)) {
                        markMfaSignOut();
                        try { await signOut(auth); } catch (_) { /* the null emission tears down */ }
                        setLoading(false);
                        return;
                    }
                    // Firebase-only fallback so the shell has a name/email to
                    // show IF it ever renders — but `profileLoaded` stays FALSE,
                    // which is the whole point (#402). This object carries no
                    // `emailVerification`/`salarySelection`, and treating that
                    // absence as "nothing outstanding" is exactly how an
                    // un-onboarded account used to reach the workspace. App.jsx
                    // now refuses to render anything protected until a real
                    // profile has landed.
                    setUser({
                        id: firebaseUser.uid,
                        username: firebaseUser.displayName || firebaseUser.email?.split('@')[0],
                        email: firebaseUser.email,
                        role: 'user'
                    });
                    setProfileError(error || new Error('profile unavailable'));
                    // Still mark as stable even with fallback
                    setAuthStable(true);
                }
            } else {
                // First-ever null (page load before persistence rehydrates)
                // → immediate transition; we'll get the real user shortly.
                // After we've seen a user, treat a null emission as POSSIBLY
                // transient — give Firebase a 1.5s grace window to re-emit
                // the real user (matches what happens during certain
                // sleep/wake handoffs). Only if the null persists do we
                // tear the session down for real.
                const doSignOut = () => {
                    pendingSignOutRef.current = null;
                    setToken(null);
                    setUser(null);
                    setAuthStable(false);
                    // The next signed-in user must re-prove their onboarding
                    // state from scratch — never inherit this one's (#402).
                    setProfileLoaded(false);
                    setProfileError(null);
                    try {
                        localStorage.removeItem('authToken');
                        localStorage.removeItem('token');
                        // Privacy: wipe ALL per-user client caches on sign-out so the
                        // next user on this browser can never inherit the previous
                        // user's private data — conversation drafts (which can hold
                        // private text), agenda last-selected contact, last book,
                        // currency preference, etc. (Major Security Breach — context
                        // must not be cached/served across users.)
                        const stale = [];
                        for (let i = 0; i < localStorage.length; i++) {
                            const k = localStorage.key(i);
                            if (k && k.startsWith('omnis:')) stale.push(k);
                        }
                        stale.forEach((k) => localStorage.removeItem(k));
                    } catch (_) { /* ignore */ }
                };
                if (hasSeenUserRef.current) {
                    if (pendingSignOutRef.current) clearTimeout(pendingSignOutRef.current);
                    pendingSignOutRef.current = setTimeout(doSignOut, 1500);
                } else {
                    doSignOut();
                }
            }
            setLoading(false);
        });

        // Refresh token periodically (Firebase tokens expire after 1 hour).
        // setInterval is suspended during system sleep; that's fine — the
        // visibility / online handlers below catch the wake-up.
        const tokenRefreshInterval = setInterval(async () => {
            const fresh = await tryRefreshFirebaseToken();
            if (fresh) setToken(fresh);
        }, 50 * 60 * 1000); // Refresh every 50 minutes (tokens last 60 min)

        // Refresh on tab focus AND on network reconnect — covers laptop-
        // lid wake (visibility flips to 'visible' the moment the user looks
        // at the screen) and the "wake but WiFi reassociating" case (online
        // fires later, once the radio's back). Both are no-ops if no user.
        const refreshIfPossible = async (reason) => {
            const fresh = await tryRefreshFirebaseToken();
            if (fresh) {
                setToken(fresh);
                console.log(`Token refreshed (${reason})`);
            }
            // Failure is intentionally non-fatal — the next API call's
            // 401 handler (App.jsx) will retry refresh and only sign out
            // if the refresh truly fails.
        };
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') refreshIfPossible('tab focus');
        };
        const handleOnline = () => refreshIfPossible('network reconnect');
        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('online', handleOnline);

        return () => {
            unsubscribe();
            clearInterval(tokenRefreshInterval);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('online', handleOnline);
            if (pendingSignOutRef.current) clearTimeout(pendingSignOutRef.current);
        };
    }, []);

    const login = async (email, password) => {
        try {
            await signInWithEmailAndPassword(auth, email, password);
            return { success: true };
        } catch (error) {
            // 2FA (#333): the password was right and the account has a second
            // factor — not a failure. The caller renders MfaChallenge with the
            // resolver; the typed email/password ride along so a recovery-code
            // reset can sign straight back in.
            if (error?.code === 'auth/multi-factor-auth-required') {
                return {
                    success: false, mfaRequired: true,
                    resolver: getMultiFactorResolver(auth, error),
                    email, password,
                };
            }
            console.error('Login failed:', error);
            let errorMessage = t('authLoginFailed', 'Login failed. Please try again.');

            switch (error.code) {
                case 'auth/invalid-email':
                    errorMessage = t('authInvalidEmail', 'Invalid email address.');
                    break;
                case 'auth/user-disabled':
                    errorMessage = t('authAccountDisabled', 'This account has been disabled.');
                    break;
                case 'auth/user-not-found':
                    errorMessage = t('authNoAccount', 'No account found with this email.');
                    break;
                case 'auth/wrong-password':
                    errorMessage = t('authWrongPassword', 'Incorrect password.');
                    break;
                case 'auth/invalid-credential':
                    errorMessage = t('authInvalidCredential', 'Invalid email or password.');
                    break;
                case 'auth/too-many-requests':
                    errorMessage = t('authTooManyAttempts', 'Too many failed attempts. Please try again later.');
                    break;
                default:
                    // Any other Firebase code is plumbing ("Firebase: Error
                    // (auth/network-request-failed)."), not a sentence for a person:
                    // the standard one (#439). The raw error is logged above.
                    errorMessage = standardErrorMessage(t);
            }

            return { success: false, error: errorMessage };
        }
    };

    const loginWithGoogle = async () => {
        try {
            await signInWithPopup(auth, googleProvider);
            return { success: true };
        } catch (error) {
            // 2FA (#333) — same as login(). No password to re-use here; the
            // email (when Firebase reports it) prefills the recovery form.
            if (error?.code === 'auth/multi-factor-auth-required') {
                return {
                    success: false, mfaRequired: true,
                    resolver: getMultiFactorResolver(auth, error),
                    email: error?.customData?.email || '', password: null,
                };
            }
            console.error('Google login failed:', error);
            let errorMessage = t('authGoogleFailed', 'Google login failed. Please try again.');

            if (error.code === 'auth/popup-closed-by-user') {
                errorMessage = t('authLoginCancelled', 'Login cancelled.');
            } else if (error.code === 'auth/popup-blocked') {
                errorMessage = t('authPopupBlocked', 'Popup was blocked. Please allow popups for this site.');
            }

            return { success: false, error: errorMessage };
        }
    };

    // #373 — the typed Username must become the account's display name. It used
    // to be accepted and silently dropped (the updateProfile call below was
    // commented out), so the backend saw no `name` claim and fell back to the
    // email prefix for every single password signup.
    //
    // Order matters and is coordinated through utils/signupIdentity:
    //   arm the barrier → create the account → set the displayName → mint a
    //   token that carries it → release the barrier.
    // The auth-state handler waits on that barrier before it fetches the
    // profile, which is what creates the backend row — so the row is named
    // correctly the first time instead of being renamed a moment later.
    const signup = async (username, email, password) => {
        const displayName = normalizeUsername(username);
        armSignupBarrier(displayName);
        try {
            const credential = await createUserWithEmailAndPassword(auth, email, password);

            if (displayName) {
                // Two steps, both required: updateProfile puts the name on the
                // Firebase account record, and a FORCED refresh is what produces
                // an ID token whose `name` claim carries it — the token minted by
                // createUserWithEmailAndPassword predates the name by definition.
                //
                // Its OWN try/catch, because by this point the account exists and
                // the user is signed in: a naming failure must never be reported
                // as "signup failed" (it isn't), and the PATCH reconciliation in
                // the auth-state handler is the fallback for exactly this.
                try {
                    await updateProfile(credential.user, { displayName });
                    await credential.user.getIdToken(true);
                } catch (nameErr) {
                    console.warn('Could not set the signup display name:', nameErr);
                }
            }

            return { success: true };
        } catch (error) {
            console.error('Signup failed:', error);
            // Nothing will reconcile a signup that never happened.
            clearPendingSignupUsername();
            let errorMessage = t('authSignupFailed', 'Signup failed. Please try again.');

            switch (error.code) {
                case 'auth/email-already-in-use':
                    errorMessage = t('authEmailInUse', 'An account with this email already exists.');
                    break;
                case 'auth/invalid-email':
                    errorMessage = t('authInvalidEmail', 'Invalid email address.');
                    break;
                case 'auth/weak-password':
                    errorMessage = t('authWeakPassword', 'Password should be at least 6 characters.');
                    break;
                default:
                    errorMessage = standardErrorMessage(t);   // #439 — see login()
            }

            return { success: false, error: errorMessage };
        } finally {
            // Always — a barrier nobody releases would stall the next sign-in
            // for its whole timeout.
            resolveSignupBarrier();
        }
    };

    // Password Recovery (#338). Firebase owns the whole reset lifecycle once the
    // email is sent: minting the secure, time-limited, single-use link, hosting
    // the "set a new password" page, and enforcing the link's expiry/one-time-use
    // — none of that is reimplemented here. The caller (ForgotPasswordPage) is
    // responsible for the anti-enumeration UX: `error.code === 'auth/user-not-found'`
    // must be presented identically to a real success, so it is surfaced (not
    // swallowed) rather than pre-decided here — this hook stays a thin,
    // honest wrapper over the SDK call. 2FA note (#333): Firebase's
    // multi-factor sign-in step is unaffected by this flow — it happens at
    // LOGIN time, not at reset time, so a reset never bypasses the challenge.
    //
    // #375 — the email itself is now OURS: the backend mints the same Firebase
    // link with the Admin SDK and delivers it through the PAI transactional
    // layout, so the user gets a centred "Reset Password" button instead of a
    // raw URL in Firebase's console template.
    //
    // The client SDK stays as the FALLBACK, and that is the load-bearing part.
    // Password reset is the recovery path for a locked-out user, so it must
    // never be the thing that is broken: whenever the backend answers
    // `not_configured` — dark SMTP (every dev/E2E environment), an Admin SDK we
    // are not permitted to use, a post-mint send failure — we send Firebase's
    // own email exactly as before. Worst case the user gets the plainer message;
    // never no message.
    //
    // Anti-enumeration is preserved on BOTH paths: the backend answers a
    // byte-identical 200 for an existing and an unknown address, and the
    // fallback keeps surfacing `auth/user-not-found` for ForgotPasswordPage to
    // render identically to a success (it is the caller's rule, not this hook's).
    const resetPassword = async (email) => {
        try {
            await apiService.post('/auth/password-reset', { email });
            return { success: true, branded: true };
        } catch (err) {
            const type = err?.body?.error?.type;
            if (type === 'invalid_email') {
                return { success: false, code: 'auth/invalid-email', error: 'Invalid email address.' };
            }
            // Anything else — not_configured, a network failure, a 5xx — falls
            // through to Firebase rather than telling a locked-out user no.
        }

        try {
            await sendPasswordResetEmail(auth, email);
            return { success: true, branded: false };
        } catch (error) {
            console.error('Password reset email failed:', error);
            return { success: false, code: error.code, error: error.message };
        }
    };

    const logout = async () => {
        try {
            await signOut(auth);
        } catch (error) {
            console.error('Logout failed:', error);
        }
    };

    // Hot reload (#397) — a profile change made in Settings reaches every
    // surface that renders identity WITHOUT a page reload.
    //
    // A window event is not merely idiomatic here, it is NECESSARY: `useAuth`
    // is a plain hook, not a context, so App, WorkspaceShell, MainOfficeView,
    // BoardsPanel, DayChannelPanel, ExpensesPanel and RulebookPanel each hold
    // their OWN independent `user` state and there is no shared store to write
    // to. Because every one of those instances registers this listener, a
    // single dispatch updates all of them at once — which is what makes the
    // rail initials, the name beside them, the mobile menu and the chat-bubble
    // avatars move together (the `omnis:plan-updated` precedent).
    //
    // `SettingsModal` has dispatched `omnis:profile-updated` since #184, under
    // the comment "Let the rest of the app (avatar/account menu) pick up the
    // new name" — and NOTHING ever listened, so every one of those surfaces
    // kept the old name until a reload. This is the missing half.
    //
    // A local MERGE, deliberately, not `refreshProfile()`: (a) the detail
    // carries the value the SERVER returned from the PATCH — already trimmed
    // and clipped to the column width — so a refetch would learn nothing;
    // (b) it is instant, where a round trip leaves stale initials on screen for
    // another beat; and (c) `refreshProfile` runs `syncProfileSideEffects`,
    // which would PATCH a still-pending signup username (#373) straight back
    // over the rename the user just made.
    useEffect(() => {
        const onProfileUpdated = (e) => {
            const detail = e?.detail || {};
            setUser((prev) => {
                if (!prev) return prev;
                let next = prev;
                for (const key of PROFILE_EVENT_FIELDS) {
                    const value = detail[key];
                    if (typeof value === 'string' && value.trim() && value !== prev[key]) {
                        if (next === prev) next = { ...prev };
                        next[key] = value;
                    }
                }
                // Same object when nothing actually moved, so a redundant or
                // unrelated dispatch re-renders none of the instances above.
                return next;
            });
        };
        window.addEventListener('omnis:profile-updated', onProfileUpdated);
        return () => window.removeEventListener('omnis:profile-updated', onProfileUpdated);
    }, []);

    const refreshToken = useCallback(async () => {
        const fresh = await tryRefreshFirebaseToken();
        if (fresh) setToken(fresh);
        return fresh;
    }, []);

    // #335 — re-read the profile after the user verifies their email, so the
    // gate in App.jsx falls away without a reload. Kept tiny and best-effort:
    // it only ever REPLACES the user object with fresher server state.
    const refreshProfile = useCallback(async () => {
        try {
            let profile = await apiService.getUserProfile();
            // Called by the verification screen (#335) and the salary screen
            // (#374) the moment their gate lifts — which is precisely when the
            // deferred plumbing PATCH becomes allowed. Retrying it here is what
            // makes "the gate refused it" a delay rather than a loss.
            profile = await syncProfileSideEffects(profile);
            setUser({
                id: profile.id,
                username: profile.username,
                email: profile.email,
                role: profile.role,
                timezone: profile.timezone || 'UTC',
                emailVerification: profile.email_verification || null,
                salarySelection: profile.salary_selection || null,
            });
            setProfileError(null);
            setProfileLoaded(true);
            return profile;
        } catch (_) {
            return null;
        }
    }, []);

    return {
        token, user, loading, authStable, profileLoaded, profileError,
        login, loginWithGoogle, signup, resetPassword, logout, refreshToken,
        refreshProfile,
    };
};

export default useAuth;
